import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from '@lb-classroom/ui';
import TutorShiftSetApiUtils from '@utils/api/tutorShiftSet';
import * as ShiftSetActions from '@actions/tutorShiftSet';
import type { Moment } from 'moment';
import type { EditShiftSetModalContainerProps } from './edit-shift-set-modal.types';

interface RootState {
  tutorShiftSet: {
    saving: boolean;
    durationEndOfChosenSet: Moment | null;
  };
}

export const useEditShiftSetModalService = (
  props: EditShiftSetModalContainerProps,
) => {
  const { hideModal, addModalMessage, shiftSet, tutorId } = props;
  const dispatch = useDispatch();

  // Read from Redux state
  const saving = useSelector((state: RootState) => state.tutorShiftSet.saving);
  const durationEnd = useSelector(
    (state: RootState) => state.tutorShiftSet.durationEndOfChosenSet,
  );

  const [focusedOnDurationEnd, setFocusedOnDurationEnd] = useState(false);

  const handleDurationEndChange = useCallback(
    (date: Moment | null) => {
      dispatch(ShiftSetActions.updateDurationEndOfChosenSet(date));
    },
    [dispatch],
  );

  const handleFocusChange = useCallback((focused: boolean) => {
    setFocusedOnDurationEnd(focused);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!tutorId || !shiftSet?.id || !durationEnd) {
        addModalMessage('Missing required data', {
          type: 'error',
          autoDisappear: false,
        });
        return;
      }

      try {
        await TutorShiftSetApiUtils.setEndDate(tutorId, shiftSet.id, durationEnd);
        toast.success('Successfully updated');
        dispatch(ShiftSetActions.setEndDateSucceeded({ shiftSetId: shiftSet.id, durationEnd }));
        hideModal();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to update shift set';
        addModalMessage(errorMessage, {
          type: 'error',
          autoDisappear: false,
        });
      }
    },
    [tutorId, shiftSet, durationEnd, dispatch, hideModal, addModalMessage],
  );

  return {
    isSubmitting: saving,
    durationEnd,
    focusedOnDurationEnd,
    message: props.message,
    messageType: props.messageType,
    onDurationEndChange: handleDurationEndChange,
    onFocusChange: handleFocusChange,
    onSubmit: handleSubmit,
    onClose: hideModal,
  };
};
